import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  createCheckoutSession,
  createPortalSession,
  deleteOrganization,
  getAuditLog,
  getBilling,
  getKeyStatus,
  mergeOrganization,
  type AuditEntry,
  type BillingState,
  type KeyStatus,
  type TeamSummary,
} from '../api';
import type { TeamJwt } from '../auth/teamJwt';
import { PanelState } from '../components/Chrome';
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  BUTTON_DANGER,
  BUTTON_PRIMARY_LG,
  BUTTON_SECONDARY_LG,
  CARD,
  FIELD_LABEL,
  FIELD_WIDE,
} from '../controlStyles';
import { billingStatusBadgeClass } from '../organization/billingStatusTone';
import { OrganizationMembers } from '../organization/OrganizationMembers';
import { OrganizationOverview } from '../organization/OrganizationOverview';
import type { AdminSection } from '../routing';
import type { OrganizationRouteContext } from './workspaceContext';

type TeamJwtProvider = () => Promise<TeamJwt>;

function BillingPanel({ getTeamJwt, team }: { getTeamJwt: TeamJwtProvider; team: TeamSummary }) {
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setBilling(null);
    setError(null);
    getTeamJwt().then(jwt => getBilling(jwt, team.orgId, controller.signal)).then(state => {
      setBilling(state);
      setSeats(Math.max(1, state.seatCount));
    }).catch(cause => {
      if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'Unable to load billing.');
    });
    return () => controller.abort();
  }, [getTeamJwt, team.orgId]);

  const handoff = async (kind: 'checkout' | 'portal') => {
    setBusy(kind);
    setError(null);
    try {
      const jwt = await getTeamJwt();
      const result = kind === 'checkout'
        ? await createCheckoutSession(jwt, team.orgId, seats)
        : await createPortalSession(jwt, team.orgId);
      window.location.assign(result.url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Billing handoff failed.');
      setBusy(null);
    }
  };

  if (error && !billing) return <PanelState tone="error"><strong>Billing is unavailable</strong><span>{error}</span></PanelState>;
  if (!billing) return <PanelState>Loading billing state…</PanelState>;
  return (
    <div className={`billing-panel ${PANEL_STACK}`}>
      {error && <div className={ALERT_ERROR} role="alert">{error}</div>}
      <section className={`plan-card ${CARD} ${SPLIT_ROW}`}>
        <div>
          <span className={FIELD_LABEL}>Current plan</span>
          <h3 className={`${CARD_TITLE} capitalize`}>{billing.plan}</h3>
          <span className={billingStatusBadgeClass(billing.status)}>{billing.status.replaceAll('_', ' ')}</span>
        </div>
        <dl className="metric-list m-0 flex gap-10">
          <div className="min-w-[7rem]"><dt className="text-[0.7rem] text-nim-muted">Seats</dt><dd className={METRIC_VALUE}>{billing.seatCount}</dd></div>
          <div className="min-w-[7rem]">
            <dt className="text-[0.7rem] text-nim-muted">Current period ends</dt>
            <dd className={METRIC_VALUE}>{billing.currentPeriodEnd ? new Date(billing.currentPeriodEnd * 1000).toLocaleDateString() : 'Not scheduled'}</dd>
          </div>
        </dl>
      </section>
      <section className={`action-card ${CARD} ${SPLIT_ROW}`}>
        <div><h3 className={CARD_TITLE}>Upgrade or change plan</h3><p className={CARD_BODY}>Checkout opens on the billing provider. The console never receives card details.</p></div>
        <label className="seat-field grid gap-1.5 text-[0.72rem] font-medium text-nim-muted">
          <span>Requested seats</span>
          <input className={`${FIELD_WIDE} w-28`} type="number" min="1" max="1000" value={seats} onChange={event => setSeats(Number(event.target.value))} />
        </label>
        <button className={BUTTON_PRIMARY_LG} type="button" disabled={busy !== null || !Number.isInteger(seats) || seats < 1} onClick={() => void handoff('checkout')}>{busy === 'checkout' ? 'Opening checkout…' : 'Upgrade / change plan'}</button>
      </section>
      <section className={`action-card ${CARD} ${SPLIT_ROW} !py-[1.15rem]`}>
        <div><h3 className={CARD_TITLE}>Manage billing</h3><p className={CARD_BODY}>Update payment methods, invoices, and an existing subscription.</p></div>
        <button className={BUTTON_SECONDARY_LG} type="button" disabled={busy !== null} onClick={() => void handoff('portal')}>{busy === 'portal' ? 'Opening portal…' : 'Manage billing'}</button>
      </section>
    </div>
  );
}

/**
 * Shapes the admin pages share. Local to this file: the members and overview
 * surfaces already have their own vocabulary in `controlStyles`, and these three
 * panels are the only remaining callers.
 */
const PANEL_STACK = 'panel-stack grid w-full gap-4';
const CARD_TITLE = 'm-0 text-[1.15rem] font-semibold tracking-[-0.015em]';
const CARD_BODY = 'mt-[0.45rem] text-[0.86rem] leading-relaxed text-nim-muted';
const METRIC_VALUE = 'mt-[0.3rem] text-[1.05rem] font-semibold';
/**
 * A heading and its action on one line. Collapses to a column on a narrow page
 * through a real `@container` at-rule in `styles.css`.
 */
const SPLIT_ROW = 'card-heading flex items-center justify-between gap-8';

function SecurityPanel({ getTeamJwt, team }: { getTeamJwt: TeamJwtProvider; team: TeamSummary }) {
  const [status, setStatus] = useState<KeyStatus | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setStatus(null);
    setAudit([]);
    setError(null);
    getTeamJwt().then(jwt => Promise.all([
      getKeyStatus(jwt, team.orgId, controller.signal),
      getAuditLog(jwt, team.orgId, controller.signal),
    ]))
      .then(([nextStatus, entries]) => {
        setStatus(nextStatus);
        setAudit(entries);
      })
      .catch(cause => {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'Unable to load security posture.');
      });
    return () => controller.abort();
  }, [getTeamJwt, team.orgId]);
  if (error) return <PanelState tone="error"><strong>Security posture is unavailable</strong><span>{error}</span></PanelState>;
  if (!status) return <PanelState>Loading security posture…</PanelState>;
  return (
    <div className={`security-panel ${PANEL_STACK}`}>
      {/* Two across, one across on a narrow page — a real `@container` at-rule. */}
      <section className="security-grid grid grid-cols-2 gap-4">
        <article className={`posture-card ${CARD}`}>
          <span className={FIELD_LABEL}>Data custody</span>
          <h3 className={`${CARD_TITLE} text-[1.3rem] capitalize`}>{status.mode.replaceAll('-', ' ')}</h3>
          <p className={CARD_BODY}>{status.mode === 'server-managed' ? 'Organization data uses managed encryption with recoverable key custody.' : 'This organization still uses its legacy custody mode.'}</p>
        </article>
        <article className={`posture-card ${CARD}`}>
          <span className={FIELD_LABEL}>Key epoch</span>
          <h3 className={`${CARD_TITLE} text-[1.3rem]`}>{status.dekEpoch ?? 'Not initialized'}</h3>
          <p className={CARD_BODY}>{status.dekFingerprint ? `Fingerprint ${status.dekFingerprint.slice(0, 16)}…` : 'No managed key fingerprint is available.'}</p>
        </article>
      </section>
      <section className={CARD}>
        <h3 className={CARD_TITLE}>Recent security activity</h3>
        <p className={CARD_BODY}>Newest organization audit events from the isolated TeamRoom.</p>
        <div className="audit-list mt-4 border-t border-nim">
          {audit.length === 0
            ? <span className="block pt-4 text-[0.72rem] text-nim-muted">No audit events are available.</span>
            : audit.map(entry => (
              <div className="audit-row flex items-center justify-between gap-4 border-b border-nim py-[0.7rem]" key={entry.id}>
                <div>
                  <strong className="block text-[0.82rem] font-semibold capitalize">{entry.action.replaceAll('.', ' ')}</strong>
                  <span className="mt-[0.15rem] block text-[0.72rem] text-nim-muted">{entry.target || 'Organization'}</span>
                </div>
                <time className="text-[0.72rem] text-nim-muted" dateTime={new Date(entry.ts).toISOString()}>{new Date(entry.ts).toLocaleString()}</time>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}

function DangerPanel({
  getTeamJwt,
  team,
  teams,
  onChanged,
  onDeleted,
}: {
  getTeamJwt: TeamJwtProvider;
  team: TeamSummary;
  teams: TeamSummary[];
  onChanged: () => Promise<void>;
  onDeleted: () => Promise<void>;
}) {
  const survivors = teams.filter(candidate => candidate.orgId !== team.orgId);
  const [survivorOrgId, setSurvivorOrgId] = useState(survivors[0]?.orgId || '');
  const [deleteAfterMerge, setDeleteAfterMerge] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const performMerge = async () => {
    setBusy('merge');
    setError(null);
    setMessage(null);
    try {
      const jwt = await getTeamJwt();
      const result = await mergeOrganization(jwt, team.orgId, survivorOrgId, deleteAfterMerge);
      if (result.partial) throw new Error(result.error || 'The merge completed only partially.');
      if (result.drainedDeleted) {
        await onDeleted();
        return;
      }
      setMessage('Merge request completed.');
      await onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Organization merge failed.');
    } finally {
      setBusy(null);
    }
  };
  const performDelete = async () => {
    setBusy('delete');
    setError(null);
    setMessage(null);
    try {
      const jwt = await getTeamJwt();
      await deleteOrganization(jwt, team.orgId);
      await onDeleted();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Organization deletion failed.');
      setBusy(null);
    }
  };
  return (
    <div className={`danger-panel ${PANEL_STACK}`}>
      {message && <div className={ALERT_SUCCESS} role="status">{message}</div>}
      {error && <div className={ALERT_ERROR} role="alert">{error}</div>}
      <section className={`danger-card ${CARD} grid gap-4`}>
        <div>
          <h3 className={CARD_TITLE}>Merge organization</h3>
          <p className={CARD_BODY}>Move projects and eligible members into another organization. Both organizations require admin access.</p>
        </div>
        <label className="grid gap-1.5 text-[0.72rem] font-medium text-nim-muted">
          <span>Surviving organization</span>
          <select className={FIELD_WIDE} value={survivorOrgId} onChange={event => setSurvivorOrgId(event.target.value)}>
            <option value="">Choose an organization</option>
            {survivors.map(candidate => <option key={candidate.orgId} value={candidate.orgId}>{candidate.name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[0.86rem] text-nim">
          <input
            className="size-auto accent-[var(--nim-primary)]"
            type="checkbox"
            checked={deleteAfterMerge}
            onChange={event => setDeleteAfterMerge(event.target.checked)}
          />
          Delete this organization after a complete merge
        </label>
        <button className={`${BUTTON_SECONDARY_LG} justify-self-start`} type="button" disabled={!survivorOrgId || busy !== null} onClick={() => void performMerge()}>{busy === 'merge' ? 'Merging…' : 'Merge organization'}</button>
      </section>
      <section className={`danger-card destructive-card ${CARD} grid gap-4 !border-[var(--nim-error)] !bg-[color-mix(in_srgb,var(--nim-error)_12%,transparent)]`}>
        <div>
          <span className={`${FIELD_LABEL} text-[var(--nim-error)]`}>Permanent action</span>
          <h3 className={`${CARD_TITLE} text-[var(--nim-error)]`}>Delete organization</h3>
          <p className={CARD_BODY}>This removes the organization and its TeamRoom data. Type the exact organization name to enable deletion.</p>
        </div>
        <label className="grid gap-1.5 text-[0.72rem] font-medium text-nim-muted">
          <span>Organization name</span>
          <input className={FIELD_WIDE} value={confirmation} onChange={event => setConfirmation(event.target.value)} placeholder={team.name} />
        </label>
        <button className={`${BUTTON_DANGER} justify-self-start`} type="button" disabled={confirmation !== team.name || busy !== null} onClick={() => void performDelete()}>{busy === 'delete' ? 'Deleting…' : 'Delete organization'}</button>
      </section>
    </div>
  );
}

function OrganizationAdmin({
  getTeamJwt,
  team,
  teams,
  section,
  reloadTeams,
  onDeleted,
}: {
  getTeamJwt: TeamJwtProvider;
  team: TeamSummary;
  teams: TeamSummary[];
  section: AdminSection;
  reloadTeams: () => Promise<void>;
  onDeleted: () => Promise<void>;
}) {
  if (section === 'billing') return <BillingPanel getTeamJwt={getTeamJwt} team={team} />;
  if (section === 'members') return <OrganizationMembers getTeamJwt={getTeamJwt} team={team} />;
  if (section === 'security') return <SecurityPanel getTeamJwt={getTeamJwt} team={team} />;
  if (section === 'danger') return <DangerPanel getTeamJwt={getTeamJwt} team={team} teams={teams} onChanged={reloadTeams} onDeleted={onDeleted} />;
  return <OrganizationOverview team={team} getTeamJwt={getTeamJwt} reloadTeams={reloadTeams} />;
}

export function AdminRoute({ section }: { section: AdminSection }) {
  const { team, teams, getTeamJwt, reloadTeams, handleDeleted } = useOutletContext<OrganizationRouteContext>();
  return (
    <OrganizationAdmin
      getTeamJwt={getTeamJwt}
      team={team}
      teams={teams}
      section={section}
      reloadTeams={reloadTeams}
      onDeleted={handleDeleted}
    />
  );
}
